import { useEffect } from "react";
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_NAME,
  absoluteUrl,
  type PageSeo,
} from "@/lib/seo";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

export function usePageSeo({ title, description, path, image, jsonLd }: PageSeo) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : DEFAULT_TITLE;
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const url = absoluteUrl(path);
    const imageUrl = absoluteUrl(image ?? DEFAULT_OG_IMAGE);

    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", "index, follow");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("name", "twitter:card", "summary");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertLink("canonical", url);

    const previousJsonLd = document.head.querySelectorAll<HTMLScriptElement>(
      'script[type="application/ld+json"][data-page-seo="true"]',
    );
    previousJsonLd.forEach((script) => script.remove());

    const parsed = jsonLdKey ? (JSON.parse(jsonLdKey) as PageSeo["jsonLd"]) : undefined;
    const graphs = parsed ? (Array.isArray(parsed) ? parsed : [parsed]) : [];
    const created = graphs.map((graph) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.pageSeo = "true";
      script.textContent = JSON.stringify(graph);
      document.head.appendChild(script);
      return script;
    });

    return () => {
      created.forEach((script) => script.remove());
    };
  }, [fullTitle, description, path, image, jsonLdKey]);
}
